import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Download } from 'lucide-react';
import apiService from '../services/api.service';
import { API_BASE_URL } from '../config/api';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/ui/page-header';

/**
 * Télécharger l'Organizer (2026-10-07).
 *
 * Les installeurs ne sont plus servis en libre accès sur logfit.eu : ils
 * sortent par l'API, sur un lien signé de 5 minutes demandé avec la session de
 * l'organisateur. La page demande le lien (l'intercepteur renouvelle la
 * session si besoin), puis laisse le navigateur suivre l'adresse : un
 * installeur de plusieurs centaines de Mo ne passe pas par la mémoire de la
 * page, et le téléchargement reste reprenable.
 */
type Platform = 'macos' | 'windows';

interface Availability {
  available: boolean;
  size_bytes?: number;
  updated_at?: string;
}

const PLATFORMS: Platform[] = ['macos', 'windows'];

const errorMessage = (err: any, fallback: string): string => {
  const d = err?.response?.data?.detail;
  return typeof d === 'string' ? d : fallback;
};

const Downloads: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [pending, setPending] = useState<Platform | null>(null);

  const query = useQuery({
    queryKey: ['organizer-downloads'],
    queryFn: () => apiService.getOrganizerDownloads() as Promise<Record<Platform, Availability>>,
  });

  const download = async (platform: Platform) => {
    setPending(platform);
    try {
      const { path } = await apiService.getOrganizerDownloadLink(platform);
      window.location.href = `${API_BASE_URL}${path}`;
    } catch (err) {
      toast.error(errorMessage(err, t('downloads.error')));
    } finally {
      setPending(null);
    }
  };

  const describe = (a?: Availability) => {
    if (!a?.available) return t('downloads.unavailable');
    const size = a.size_bytes ? `${Math.round(a.size_bytes / 1_000_000)} Mo` : '';
    const date = a.updated_at ? new Date(a.updated_at).toLocaleDateString(i18n.language) : '';
    return [size, date && t('downloads.updated', { date })].filter(Boolean).join(' · ');
  };

  return (
    <div className="space-y-6 p-4 md:p-8">
      <PageHeader title={t('downloads.title')} />
      <p className="max-w-2xl text-sm text-muted-foreground">{t('downloads.intro')}</p>

      {query.isError ? (
        <p className="text-sm text-destructive">{errorMessage(query.error, t('downloads.error'))}</p>
      ) : (
        <div className="max-w-2xl divide-y divide-border border-y border-border">
          {PLATFORMS.map((platform) => {
            const a = query.data?.[platform];
            return (
              <div key={platform} className="flex flex-wrap items-center gap-4 py-4">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-foreground">{t(`downloads.${platform}`)}</div>
                  <div className="text-sm text-muted-foreground">
                    {query.isLoading ? '…' : describe(a)}
                  </div>
                </div>
                <Button
                  variant="secondary"
                  disabled={!a?.available || pending !== null}
                  onClick={() => download(platform)}
                >
                  <Download size={16} className="mr-2" />
                  {pending === platform ? t('downloads.preparing') : t('downloads.button')}
                </Button>
              </div>
            );
          })}
        </div>
      )}

      <p className="max-w-2xl text-xs text-muted-foreground">{t('downloads.personal')}</p>
    </div>
  );
};

export default Downloads;
